import {useState, useRequestHeaders} from '#app';

/**
 * The DEVICE's locale, agreed between server and client.
 *
 * Dates follow the device a person is holding, exactly as the Flutter app does
 * (`currentLocaleTag()` there). The trick on the web is that the server renders
 * the page first and cannot see `navigator.language` — but every browser
 * announces the device locale on each request as the Accept-Language header.
 * So the server resolves the locale from that header once, and `useState`
 * carries the answer into the client, which renders with the SAME value:
 * no hydration mismatch, no flash of the wrong order.
 *
 * On routes rendered client-side only (`/chat**`), the initialiser runs in the
 * browser and reads `navigator.language` — the same fact from the same device.
 *
 * Junk values ("*", bot headers) are harmless: `useDates.resolveLocale` treats
 * anything unusable as en-GB, the product's historic behaviour.
 *
 * This is deliberately NOT the nuxt-i18n locale. The i18n files choose WORDS
 * ("fixture" vs "match") and only ship English variants, so binding dates to
 * them forced every non-English device down to UK ordering. Dates are about
 * the reader's own conventions, not our translation coverage.
 */
export function useDeviceLocale() {
  return useState('deviceLocale', () => {
    if (import.meta.server) {
      const header = useRequestHeaders(['accept-language'])['accept-language'] || '';
      return header.split(',')[0]?.trim() || null;
    }
    return (typeof navigator !== 'undefined' && navigator.language) || null;
  });
}
